import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ITask } from 'app/interfaces/task.interface';
import { environment } from 'environments/environment';
import { IContactFollowUpInfo } from 'modules/contacts/interfaces/contact-follow-up.interface';
import { IJourneyConfiguration } from 'modules/journeys/interfaces/journey.interface';
import { ServiceEvolutionQuality } from 'modules/promptuary/components/service-evolution-quality-banner/service-evolution-quality-banner.component';
import { ICGIRegistered } from 'modules/promptuary/interfaces/cgi-registered.interface';
import {
	CGIBaselineAnswer,
	Icgi,
} from 'modules/promptuary/interfaces/cgi.interface';
import { IIcd } from 'modules/promptuary/interfaces/icd.interface';
import { GetPromptuaryResponseV2 } from 'modules/promptuary/interfaces/promptuary.interface';
import { Observable } from 'rxjs';
import {
	IPromptuary,
	IPromptuaryHistoryParams,
} from 'shared/interfaces/promptuary.interface';

@Injectable()
export class PromptuaryService {
	serviceUrl = environment.servicesUrl;
	constructor(private httpClient: HttpClient) {}

	public createPromptuary(payload: { customerId: string; eventId: string }) {
		return this.httpClient.post<GetPromptuaryResponseV2>(
			`${this.serviceUrl}/promptuary/v2/promptuary`,
			payload
		);
	}

	public getData(
		appointmentId: string,
		customerId: string
	): Observable<IPromptuary> {
		return this.httpClient.get<IPromptuary>(
			`${this.serviceUrl}/promptuary/v2/promptuary/${appointmentId}/${customerId}`
		);
	}

	public getCGI(appointmentId: string, clientId: string): Observable<Icgi[]> {
		return this.httpClient.get<Icgi[]>(
			`${this.serviceUrl}/promptuary/v1/cgi/questions/${clientId}/${appointmentId}`
		);
	}

	public getCGIAnswers(
		appointmentId: string,
		customerId: string
	): Observable<ICGIRegistered[]> {
		return this.httpClient.get<ICGIRegistered[]>(
			`${this.serviceUrl}/promptuary/v1/cgi/registered/${appointmentId}/${customerId}`
		);
	}

	public saveCGI(
		questionId: number,
		optionId: number,
		eventId: string,
		customerId: string
	): Observable<{ ok: boolean }> {
		const payload = {
			answers: [{ questionId, optionId }],
			eventId,
			customerId,
		};

		return this.httpClient.post<{ ok: boolean }>(
			`${this.serviceUrl}/promptuary/v1/cgi/save`,
			payload
		);
	}

	public autoSaveField(newPromptuary: IPromptuary): Observable<IPromptuary> {
		return this.httpClient.post<IPromptuary>(
			`${this.serviceUrl}/promptuary/v2/promptuary/auto-save`,
			newPromptuary
		);
	}

	public registerDocument(
		eventId: string,
		customerId: string,
		hasQuality?: boolean
	): Observable<IPromptuary> {
		return this.httpClient.post<IPromptuary>(
			`${this.serviceUrl}/promptuary/v2/promptuary/register`,
			{
				eventId,
				customerId,
				...(typeof hasQuality != 'undefined' && { hasQuality }),
			}
		);
	}

	public getIcdOptions(query?: string): Observable<IIcd[]> {
		const reqPath = `${this.serviceUrl}/promptuary/v2/promptuary/cid10/search`;

		return this.httpClient.get<IIcd[]>(
			query ? reqPath + `?search=${query}` : reqPath
		);
	}

	public getPromptuaryHistory(
		params: IPromptuaryHistoryParams
	): Observable<any> {
		const reqPath = `${this.serviceUrl}/promptuary/v2/promptuary/documentary-record/search`;
		return this.httpClient.get<any>(reqPath, { params: { ...params } });
	}

	public getPromptuaryPendencies() {
		const reqPath = `${this.serviceUrl}/promptuary/professional/pendencies`;
		return this.httpClient.get<any>(reqPath);
	}

	public getServiceEvolutionQuality(
		serviceEvolution: string
	): Observable<{ isApproved: boolean }> {
		return this.httpClient.post<{ isApproved: boolean }>(
			`${this.serviceUrl}/promptuary/v2/promptuary/service-evolution/validate`,
			{
				serviceEvolution,
			}
		);
	}

	public getCGIBaselineAnswer(
		customerId: string
	): Observable<CGIBaselineAnswer> {
		return this.httpClient.get<CGIBaselineAnswer>(
			`${this.serviceUrl}/promptuary/v1/cgi/baseline/answer/${customerId}`
		);
	}

	public hasQuality(quality: ServiceEvolutionQuality): boolean {
		return quality === 'approved';
	}

	public getOutcomeForwardJourneyConfiguration(userId) {
		return this.httpClient.get<IJourneyConfiguration>(
			`${this.serviceUrl}/journeys/coordinated-care/configurations/office/users/${userId}`
		);
	}

	public getContactFollowUpInfo(customerId) {
		return this.httpClient.get<IContactFollowUpInfo>(
			`${this.serviceUrl}/promptuary/v1/cgi/evolution-and-top-reasons/${customerId}`
		);
	}

	public canRegister(appointmentId: string) {
		return this.httpClient.get<{
			canRegister: boolean;
			reason?: {
				title: string;
				description: string;
			};
		}>(
			`${this.serviceUrl}/promptuary/v2/promptuary/event/${appointmentId}/can-register`
		);
	}
	//PROMPTUARY TASKS

	public getProfessionalTask(eventId, customerId): Observable<ITask> {
		return this.httpClient.get<ITask>(
			`${this.serviceUrl}/professional/professional-tasks/${eventId}/${customerId}`
		);
	}

	public saveProfessionalTask(task: ITask): Observable<ITask> {
		return this.httpClient.post<ITask>(
			`${this.serviceUrl}/professional/professional-tasks/auto-save`,
			task
		);
	}

	public registerProfessionalTask(eventId, customerId): Observable<ITask> {
		return this.httpClient.post<ITask>(
			`${this.serviceUrl}/professional/professional-tasks/register`,
			{
				eventId,
				customerId,
			}
		);
	}

	//TASK LINKS

	public deleteTaskLink(linkId): Observable<any> {
		return this.httpClient.delete(
			`${this.serviceUrl}/professional/professional-tasks-attachments/${linkId}`
		);
	}

	public createTaskLink(link, taskId) {
		return this.httpClient.post(
			`${this.serviceUrl}/professional/professional-tasks-attachments`,
			{
				professionalTaskId: taskId,
				attachment: [
					{
						type: 'external_link',
						specifications: {
							url: link,
							description: '',
						},
					},
				],
			}
		);
	}
}
